
import m, { RequestOptions } from "mithril";
import { GlobalState } from "./globalstate";
import { LoginData, loginUserByEmail } from "./login";
import { serverUrl } from "./requestWithoutAuth";
import { loginFirebase, isFirebaseAuthenticated } from "./channel";
import { saveAuthSettings, clearAuthSettings } from "./authSettings";

// ============================================================================
// Configuration constants for login protection
// ============================================================================

// Maximum number of login attempts before giving up
const MAX_LOGIN_RETRIES = 3;

// Minimum time between login attempts (milliseconds) - prevents rapid-fire retries
const MIN_LOGIN_INTERVAL_MS = 500;

// Initial backoff delay for retries (milliseconds) - doubles each retry
const INITIAL_BACKOFF_MS = 500;

// Maximum backoff delay (milliseconds) - caps exponential growth
const MAX_BACKOFF_MS = 5000;

// Circuit breaker timeout (milliseconds) - how long to wait after max retries
const CIRCUIT_BREAKER_TIMEOUT_MS = 2 * 60 * 1000; // 2 minutes

// ============================================================================
// Global state for authentication and retry protection
// ============================================================================

// Active login promise - prevents concurrent login attempts
let authPromise: Promise<LoginData> | null = null;

// Retry tracking
let loginAttemptCount = 0;
let lastLoginAttemptTime = 0;

// Circuit breaker state
let circuitBreakerOpen = false;
let circuitBreakerResetTime = 0;

// ============================================================================
// Custom error classes
// ============================================================================

class AuthenticationError extends Error {
  constructor() {
    super("Authentication required");
    this.name = "AuthenticationError";
  }
}

class LoginThrottledError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "LoginThrottledError";
  }
}

// ============================================================================
// Helper functions
// ============================================================================

/**
 * Delays execution for the specified number of milliseconds
 */
const delay = (ms: number): Promise<void> =>
  new Promise(resolve => setTimeout(resolve, ms));

/**
 * Calculates exponential backoff delay based on attempt number
 */
const calculateBackoff = (attemptNumber: number): number => {
  const backoff = INITIAL_BACKOFF_MS * Math.pow(2, attemptNumber - 1);
  return Math.min(backoff, MAX_BACKOFF_MS);
};

/**
 * Resets all retry and circuit breaker state to initial values
 */
const resetLoginState = (): void => {
  loginAttemptCount = 0;
  circuitBreakerOpen = false;
  circuitBreakerResetTime = 0;
};

/**
 * Opens the circuit breaker to prevent further login attempts
 */
const openCircuitBreaker = (): void => {
  circuitBreakerOpen = true;
  circuitBreakerResetTime = Date.now() + CIRCUIT_BREAKER_TIMEOUT_MS;
  loginAttemptCount = 0;
  console.error(`Circuit breaker opened. Login attempts blocked until ${new Date(circuitBreakerResetTime).toLocaleTimeString()}`);
};

/**
 * Checks if circuit breaker should be reset based on timeout
 */
const checkCircuitBreakerReset = (): void => {
  if (circuitBreakerOpen && Date.now() >= circuitBreakerResetTime) {
    resetLoginState();
  }
};

// ============================================================================
// Authentication flow
// ============================================================================

/**
 * Internal function to ensure authentication with comprehensive retry protection
 *
 * Protection mechanisms:
 * - Circuit breaker: Stops all attempts after max retries for a cooldown period
 * - Retry limits: Maximum number of consecutive failed attempts
 * - Exponential backoff: Increasing delays between retries
 * - Rate limiting: Minimum time between attempts
 * - Request deduplication: Only one login attempt at a time
 */
const ensureAuthenticated = async (state: GlobalState): Promise<void> => {
  // Check and potentially reset circuit breaker
  checkCircuitBreakerReset();

  // Circuit breaker check - fail fast if too many recent failures
  if (circuitBreakerOpen) {
    const waitMinutes = Math.ceil((circuitBreakerResetTime - Date.now()) / 60000);
    throw new LoginThrottledError(
      `Too many failed login attempts. Please wait ${waitMinutes} minute(s) before trying again.`
    );
  }

  // If login is already in progress, wait for it to complete
  if (authPromise) {
    await authPromise;
    return;
  }

  // Rate limiting - prevent rapid-fire retries
  const timeSinceLastAttempt = Date.now() - lastLoginAttemptTime;
  if (timeSinceLastAttempt < MIN_LOGIN_INTERVAL_MS && loginAttemptCount > 0) {
    console.warn(`Login attempt too soon (${timeSinceLastAttempt}ms since last attempt)`);
    throw new LoginThrottledError("Please wait a moment before trying again.");
  }

  // Retry loop with exponential backoff
  let continueTrying = true;
  while (continueTrying && loginAttemptCount < MAX_LOGIN_RETRIES) {
    continueTrying = false;
    loginAttemptCount++;
    lastLoginAttemptTime = Date.now();

    // Apply exponential backoff for retries (skip delay on first attempt)
    if (loginAttemptCount >= 2) {
      const backoffMs = calculateBackoff(loginAttemptCount - 1);
      console.warn(`Login retry ${loginAttemptCount}/${MAX_LOGIN_RETRIES}, waiting ${backoffMs}ms...`);
      await delay(backoffMs);
    }

    // Start new login attempt
    authPromise = loginUserByEmail(state);

    try {
      const result = await authPromise;

      // Handle expired token
      if (result.status === "expired") {
        if (state.tokenExpired) {
          // Token refresh callback is available
          try {
            // Call the token refresh callback
            state.tokenExpired();
            continueTrying = true;
            clearAuthSettings();
            continue;
          } catch (error) {
            console.error("Token refresh failed:", error);
            clearAuthSettings();
            throw new Error(`Failed to refresh authentication token: ${error instanceof Error ? error.message : String(error)}`);
          }
        }

        // No refresh callback available
        clearAuthSettings();
        throw new Error("Authentication token has expired. Please log in again.");
      }

      // Handle other non-success statuses
      if (result.status !== "success") {
        clearAuthSettings();
        throw new Error(`Authentication failed: ${result.message || result.status}`);
      }

      // ========================================================================
      // Success! Update global state and persist authentication
      // ========================================================================

      // Update the user's ID to the internal one used by the backend and Firebase
      state.netskraflUserId = result.user_id || state.netskraflUserId;
      state.account = result.account || state.netskraflUserId;
      state.userNick = result.nickname || state.userNick;
      state.firebaseApiKey = result.firebase_api_key || state.firebaseApiKey;

      // Load state flags and preferences
      state.beginner = result.prefs?.beginner ?? true;
      state.fairPlay = result.prefs?.fairplay ?? false;
      state.ready = result.prefs?.ready ?? true;
      state.readyTimed = result.prefs?.ready_timed ?? true;
      state.audio = result.prefs?.audio ?? false;
      state.fanfare = result.prefs?.fanfare ?? false;
      state.hasPaid = result.prefs?.haspaid ?? false;

      // Save authentication settings to sessionStorage
      saveAuthSettings({
        userEmail: state.userEmail,
        netskraflUserId: state.netskraflUserId,
        userNick: state.userNick,
        firebaseApiKey: state.firebaseApiKey,
        beginner: state.beginner,
        fairPlay: state.fairPlay,
        ready: state.ready,
        readyTimed: state.readyTimed,
        audio: state.audio,
        fanfare: state.fanfare,
      });

      // Redraw UI to reflect authentication changes
      m.redraw();

      // Log in to Firebase with the token from server
      await loginFirebase(state, result.firebase_token);

      // Success - reset all retry state
      resetLoginState();

    } catch (error) {
      // On last retry, open circuit breaker
      if (loginAttemptCount >= MAX_LOGIN_RETRIES) {
        openCircuitBreaker();
        throw new Error(
          `Login failed after ${MAX_LOGIN_RETRIES} attempts. ` +
          `Please wait ${Math.ceil(CIRCUIT_BREAKER_TIMEOUT_MS / 60000)} minutes before trying again.`
        );
      }

      // Re-throw error if we're not retrying
      if (!continueTrying) {
        throw error;
      }

      // Continue to next retry iteration
    } finally {
      // Reset the promise so future attempts can proceed
      authPromise = null;
    }
  }

  // Should not reach here, but handle max retries edge case
  if (loginAttemptCount >= MAX_LOGIN_RETRIES) {
    openCircuitBreaker();
    throw new Error("Maximum login attempts exceeded. Please try again later.");
  }
};

// Internal authenticated request function
const authenticatedRequest = async <T>(
  state: GlobalState,
  options: RequestOptions<T> & { url: string },
  retries: number = 0,
): Promise<T> => {
  // Before making the request, check if Firebase needs authentication
  // This handles the case where the user returns with a valid backend cookie but expired Firebase auth
  if (!retries && !isFirebaseAuthenticated(state)) {
    // Firebase is not authenticated, ensure authentication before making the request
    await ensureAuthenticated(state);
  }
  try {
    // Make the actual request
    // console.log("Making authenticated request to", options.url);
    return await m.request<T>({
      withCredentials: true,
      ...options,
      url: serverUrl(state, options.url),
      extract: (xhr) => {
        // Check for 401 Unauthorized
        if (xhr.status === 401) {
          // console.log("Received 401 Unauthorized, triggering re-authentication");
          throw new AuthenticationError();
        }
        // Handle empty responses
        if (!xhr.responseText) {
          return null as T;
        }
        // Parse JSON response
        try {
          return JSON.parse(xhr.responseText);
        } catch (e) {
          // If JSON parsing fails, return the raw text
          return xhr.responseText as T;
        }
      }
    });
  } catch (error) {
    if (error instanceof AuthenticationError && !retries) {
      // Backend returned 401, perform full authentication and retry (force=true)
      await ensureAuthenticated(state);
      // Retry the original request
      return authenticatedRequest(state, options, retries + 1);
    }
    // Re-throw other errors
    throw error;
  }
};

export const request = <T>(state: GlobalState, options: RequestOptions<T> & { url: string }) => {
  // Enhanced request with automatic authentication handling
  return authenticatedRequest(state, options);
};

export const requestMoves = <T>(state: GlobalState, options: RequestOptions<T> & { url: string }) => {
  // Call the moves service on the Google App Engine backend
  const url = `${state.movesUrl}${options.url}`;
  const headers = {
    "Content-Type": "application/json; charset=UTF-8",
    Authorization: `Bearer ${state.movesAccessKey}`,
    ...options?.headers,
  };
  
  return m.request<T>({
    withCredentials: false,
    method: "POST",
    ...options,
    url,
    headers,
  });
};
